import { AddressModel, Country, State } from '@tradingblock/types';
import _ from 'lodash';
import { RE_PO_BOX, RE_POSTAL_CODE, ASCII_AUDIT_RANGE } from '@tradingblock/components';
import { getRequiredError, getInvalidError, getInvalidLengthError, uspsVerification } from './Validation';

export interface AddressValidationOptions {
  /** Field prefix for error keys (e.g., "address." or "duplicateStatementsAddress.") */
  fieldPrefix?: string;
  /** Storage key for USPS response */
  storageKey: string;
  /** API token for USPS verification */
  apiToken?: string;
  /** Whether this is a mailing address (PO Box allowed) */
  isMailing?: boolean;
  /** Whether to trigger USPS verification automatically */
  triggerUspsVerification?: boolean;
}

export interface AddressValues {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

/**
 * Normalizes an address string for comparison (trim, single spaces, uppercase)
 */
const normalizeAddress = (addr: string | null | undefined): string => {
  if (!addr) return '';
  return addr
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
};

/**
 * Formats an address string to title case
 */
const formatAddress = (addr: string | null | undefined): string | null => {
  if (!addr) return null;
  return addr
    .toLowerCase()
    .split(' ')
    .map(s => s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

/**
 * Validates an address and returns validation errors
 *
 * @param address - Address values to validate (can be flat or nested)
 * @param options - Validation options
 * @returns Object with validation errors keyed by field name
 */
export const validateAddressFields = (
  address: AddressValues,
  options: AddressValidationOptions
): Record<string, string | undefined> => {
  const { fieldPrefix = '', storageKey, apiToken, isMailing = false, triggerUspsVerification = true } = options;

  const prefix = (key: string) => `${fieldPrefix}${key}`;

  // Clear USPS storage if address fields are cleared or state is Puerto Rico
  if (!address.address1 || !address.city) {
    sessionStorage.setItem(storageKey, 'undefined');
  }

  if (address.state === State.PuertoRico) {
    sessionStorage.setItem(storageKey, 'undefined');
    return {};
  }

  const stateOrPostalCodeRequired = address.country === Country.UnitedStatesOfAmerica;

  // Get USPS response from storage
  let uspsResponse: any = null;
  let isValidAddress = false;
  const uspsResponseFromStorage = sessionStorage.getItem(storageKey);

  if (uspsResponseFromStorage && uspsResponseFromStorage !== 'undefined') {
    try {
      uspsResponse = JSON.parse(uspsResponseFromStorage);
      const { city, state, zip5, error, uspsAddress1 } = uspsResponse;

      if (!error && city && state && zip5) {
        const formattedCityCapitalized = _.capitalize(city);
        const startCaseCity = _.startCase(formattedCityCapitalized);
        const isCityEqual = _.toLower(startCaseCity) === _.toLower(address.city || '');
        const userZip5 = address.postalCode ? address.postalCode.split('-')[0].trim() : '';
        const isZipEqual = zip5 === userZip5;
        const isStateEqual = state === address.state;
        const isAddressEqual = normalizeAddress(uspsAddress1) === normalizeAddress(address.address1);
        isValidAddress = isCityEqual && isZipEqual && isStateEqual && isAddressEqual;
      }
    } catch (e) {
      // Invalid JSON, ignore
    }
  }

  // Trigger USPS verification if address is invalid and required fields are present
  // Only trigger if we don't have a valid cached result to avoid unnecessary API calls
  if (
    triggerUspsVerification &&
    address.address1 &&
    address.city &&
    address.state &&
    address.country === Country.UnitedStatesOfAmerica &&
    !isValidAddress &&
    address.state !== State.PuertoRico
  ) {
    // Check cache again before making API call (in case it was updated)
    const cachedResponse = sessionStorage.getItem(storageKey);
    let shouldMakeCall = true;

    if (cachedResponse && cachedResponse !== 'undefined') {
      try {
        const cached = JSON.parse(cachedResponse);
        const normalize = (str: string) => (str || '').trim().toUpperCase();
        // If cache matches current address, don't make API call
        if (
          cached.uspsAddress1 &&
          normalize(cached.uspsAddress1) === normalize(address.address1 || '') &&
          cached.city &&
          normalize(cached.city) === normalize(address.city || '') &&
          cached.state &&
          normalize(cached.state) === normalize(address.state || '')
        ) {
          shouldMakeCall = false;
        }
      } catch (e) {
        // Invalid cache, continue to make API call
      }
    }

    if (shouldMakeCall) {
      const getUSPS = async () => {
        await new Promise(resolve => setTimeout(resolve, 100));
        const addressModel: AddressModel = {
          address1: address.address1 || '',
          address2: address.address2 || '',
          city: address.city || '',
          state: address.state || '',
          postalCode: address.postalCode || '',
          country: address.country || '',
        };
        return await uspsVerification(addressModel, apiToken);
      };

      getUSPS().then(result => {
        if (result) {
          sessionStorage.setItem(storageKey, JSON.stringify(result));
        }
      });
    }
  }

  // Build base errors object
  const errors: Record<string, string | undefined | null> = {
    [prefix('country')]: getRequiredError(address.country),
    [prefix('address1')]:
      getRequiredError(address.address1) ||
      (!isMailing && getInvalidError(!RE_PO_BOX.test(address.address1 || ''))) ||
      getInvalidError(ASCII_AUDIT_RANGE.test(address.address1 || '')) ||
      getInvalidLengthError((address.address1 || '').length <= 30) ||
      (address.address1 && getInvalidError(_.trim(address.address1) !== '')),
    [prefix('postalCode')]:
      getRequiredError(stateOrPostalCodeRequired ? address.postalCode : true) ||
      getInvalidError(stateOrPostalCodeRequired ? RE_POSTAL_CODE.test(address.postalCode || '') : true),
    [prefix('city')]: getRequiredError(address.city),
    [prefix('state')]: getRequiredError(stateOrPostalCodeRequired ? address.state : true),
  };

  // Validate address2 if provided
  if (address.address2 && address.address2.trim() !== '') {
    errors[prefix('address2')] =
      (!isMailing && getInvalidError(!RE_PO_BOX.test(address.address2))) ||
      getInvalidError(ASCII_AUDIT_RANGE.test(address.address2)) ||
      getInvalidLengthError(address.address2.length <= 30) ||
      getInvalidError(_.trim(address.address2) !== '');
  }

  // Add USPS validation errors if response exists
  if (uspsResponse) {
    const {
      city,
      state,
      zip5,
      error,
      uspsAddress1,
      uspsAddress2,
      isPoBox,
      isSecondaryInformationRequired,
      isSecondaryInformationNotRecognized,
    } = uspsResponse;

    // Handle USPS error response
    if (error && address.address1 && address.city && address.state && address.postalCode) {
      const errorMessage = `${error.description.trim()} Please review the entered address.`;
      return {
        [prefix('postalCode')]: errorMessage,
        [prefix('city')]: errorMessage,
        [prefix('state')]: errorMessage,
        [prefix('address1')]: errorMessage,
      };
    }

    // Add USPS validation errors
    if (city && state && zip5) {
      const formattedCityCapitalized = _.capitalize(city);
      const startCaseCity = _.startCase(formattedCityCapitalized);
      const isCityEqual = _.toLower(startCaseCity) === _.toLower(address.city || '');
      const userZip5 = address.postalCode ? address.postalCode.split('-')[0].trim() : '';
      const isZipEqual = zip5 === userZip5;
      const isStateEqual = state === address.state;
      const normalizedUspsAddress1 = normalizeAddress(uspsAddress1);
      const normalizedUserAddress1 = normalizeAddress(address.address1);
      const normalizedUspsAddress2 = normalizeAddress(uspsAddress2);
      const normalizedUserAddress2 = normalizeAddress(address.address2);

      const isAddress1Equal = normalizedUspsAddress1 === normalizedUserAddress1;
      const isAddress2Equal = normalizedUspsAddress2 ? normalizedUspsAddress2 === normalizedUserAddress2 : true;

      const formattedAddress1 = formatAddress(uspsAddress1);
      const formattedAddress2 = formatAddress(uspsAddress2);

      // PO Box validation
      if (isPoBox && !isMailing) {
        errors[prefix('address1')] = 'PO Box addresses are not allowed for this address type.';
      }

      // Secondary information required
      // Only require address2 if USPS provides it separately (not combined into address1)
      if (isSecondaryInformationRequired && !address.address2 && uspsAddress2) {
        errors[prefix('address2')] =
          'Secondary address information (apartment, suite, etc.) is required for this address.';
      }

      // Secondary information not recognized
      if (isSecondaryInformationNotRecognized && address.address2) {
        errors[prefix('address2')] =
          'The secondary address information provided was not recognized. Please verify or remove it.';
      }

      // USPS formatting errors
      if (!isCityEqual) {
        errors[prefix('city')] = `Please verify city. USPS Provided City: ${startCaseCity}`;
      }
      if (!isZipEqual) {
        errors[prefix('postalCode')] = `Please verify the zip code. USPS Provided Zip Code: ${zip5}`;
      }
      if (!isStateEqual) {
        errors[prefix('state')] = `Please verify the state. USPS Provided State: ${state}`;
      }
      if (!isAddress1Equal && formattedAddress1) {
        errors[prefix('address1')] = `Please utilize the USPS provided formatting: ${formattedAddress1}`;
      }
      if (normalizedUspsAddress2 && !isAddress2Equal && formattedAddress2) {
        errors[prefix('address2')] = `Please utilize the USPS provided formatting: ${formattedAddress2}`;
      }
    }
  }

  // Remove undefined errors
  Object.keys(errors).forEach(key => errors[key] === undefined && delete errors[key]);
  return errors as Record<string, string | undefined>;
};

/**
 * Gets USPS response from storage for use in UI (e.g., "Use USPS provided address" button)
 */
export const getUspsResponseFromStorage = (storageKey: string) => {
  const uspsResponseFromStorage = sessionStorage.getItem(storageKey);
  if (!uspsResponseFromStorage || uspsResponseFromStorage === 'undefined') {
    return null;
  }

  try {
    const uspsResponse = JSON.parse(uspsResponseFromStorage);
    if (uspsResponse && !uspsResponse.error) {
      const { city, state, zip5, zip4, uspsAddress1, uspsAddress2 } = uspsResponse;
      const uspsPostalCode = zip4 ? `${zip5}-${zip4}` : zip5;
      return {
        address1: uspsAddress1 || '',
        address2: uspsAddress2 || '',
        city: city || '',
        state: state || '',
        postalCode: uspsPostalCode || '',
      };
    }
  } catch (e) {
    // Invalid JSON, ignore
  }

  return null;
};

/**
 * Checks if USPS combined addresses (address2 is required but not provided separately)
 */
export const isUspsAddressCombined = (storageKey: string): boolean => {
  const uspsResponseFromStorage = sessionStorage.getItem(storageKey);
  if (!uspsResponseFromStorage || uspsResponseFromStorage === 'undefined') {
    return false;
  }

  try {
    const uspsResponse = JSON.parse(uspsResponseFromStorage);
    if (uspsResponse && !uspsResponse.error) {
      const { isSecondaryInformationRequired, uspsAddress2 } = uspsResponse;
      // If secondary info is required but USPS didn't provide separate address2,
      // it means USPS combined it into address1
      return isSecondaryInformationRequired === true && (!uspsAddress2 || uspsAddress2 === null);
    }
  } catch (e) {
    // Invalid JSON, ignore
  }

  return false;
};
